import {
  Directive,
  OnChanges,
  Input,
  SimpleChanges,
  HostBinding,
  OnInit,
  ElementRef,
  Renderer2,
  AfterViewInit,
} from '@angular/core';

import {
  BTN,
  COLOR,
  Color,
  FILL,
  SIZE,
  Shape,
  Size,
  Fill,
  TextTransform,
  TEXT_TRANSFORM,
} from '../constants';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';

@Directive({
  selector: '[sch-button]',
  standalone: true,
})
export class ButtonDirective implements OnChanges, OnInit, AfterViewInit {
  constructor(private el: ElementRef,
    private rdr: Renderer2,
    private sanitizer: DomSanitizer) { }

  @Input() variant: Fill = FILL.SOLID;

  @HostBinding('attr.aria-label')
  @Input() description = '';

  @Input() shape: Shape = BTN.SHAPE.NORMAL;
  @Input() textTransform: TextTransform = TEXT_TRANSFORM.CAPITALIZE;
  @Input() color: Color | string = COLOR.PRIMARY;
  @Input() fontColor?: Color | string;
  @Input() size: Size = SIZE.MEDIUM;
  @Input() hasShadow = false;
  @Input() hideBorder = false;

  @HostBinding('class') hostElementClasses = this.getHostElementClasses();
  @HostBinding('style') get getHostElementStyle(): SafeStyle | null {
    if (!this.hideBorder) {
      let style = `border: 1px solid`;
      if (!this.isColorLiteral(this.color)) {
        style = `${style}; border-color: ${this.color}`;
      }
      return this.sanitizer.bypassSecurityTrustStyle(style);
    }
    return null;
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.hostElementClasses = this.getHostElementClasses();
  }

  ngOnInit(): void {
    this.rdr.setAttribute(this.el.nativeElement, 'type', 'button');
  }

  ngAfterViewInit(): void {
    const innerText = this.el.nativeElement.innerText;
    const innerHTML = this.el.nativeElement.innerHTML;

    if (!innerText && !innerHTML && this.shape  !== BTN.SHAPE.CIRCLE) {
      this.rdr.addClass(this.el.nativeElement, 'btn-floating');
      this.rdr.addClass(this.el.nativeElement, 'square-btn');
    }

    if(this.fontColor) {
      if (this.isColorLiteral(this.fontColor)) {
        this.rdr.addClass(this.el.nativeElement, `text-${this.fontColor}`)
      } else {
        this.rdr.setStyle(this.el.nativeElement, 'color', this.fontColor);
      }
    } else {
      const fontColor = this.variant === FILL.SOLID ? '#ffffff' : this.color;
      if (this.isColorLiteral(fontColor)) {
        this.rdr.addClass(this.el.nativeElement, `text-${fontColor}`)
      } else {
        this.rdr.setStyle(this.el.nativeElement, 'color', fontColor);
      }
    }
  }

  isColorLiteral(color: Color | string): boolean {
    const isColorLiteral = Object.values(COLOR).some(x => x === color);
    return isColorLiteral;
  }

  private getHostElementClasses(): string {
    let classes = `btn btn-${this.size} text-${this.textTransform} normal-btn-radius`;
    const element = this.el.nativeElement as HTMLElement;

    if (this.isColorLiteral(this.color) && !this.hideBorder) {
      classes = `${classes} border-${this.color}`;
    }

    if(this.variant === FILL.SOLID) {
      if (this.isColorLiteral(this.color)) {
        classes = `${classes} btn-${this.color}`
      } else {
        element.style.backgroundColor = this.color;
      }
    }
    
    if (this.variant === FILL.SHADED) {
      classes = `${classes} shaded-btn`;
    }

    if (!this.hasShadow) {
      classes = `${classes} shadow-none`;
    }

    if (this.shape === BTN.SHAPE.CIRCLE) {
      classes = `${classes} btn-floating`;
    }

    if (this.shape === BTN.SHAPE.ROUNDED) {
      classes = `${classes} btn-${this.shape}`;
    }

    return classes;
  }
}
